import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Shared } from '@rubii/frontend-common';

export interface OtpState {
  invalidCounter: number;
  pausedUntil: number; // ms since epoch
}

@Injectable({ providedIn: 'root' })
export class OtpStateService {
  private readonly cookieKey = 'smsOtpState';
  private stateSubject = new BehaviorSubject<OtpState>({ invalidCounter: 0, pausedUntil: 0 });
  state$ = this.stateSubject.asObservable();

  constructor() {
    this.loadFromCookie();
  }

  private loadFromCookie() {
    try {
      const raw = Shared.helpers.CookieHelper.getCookie(this.cookieKey) || null;
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') {
        this.stateSubject.next({ invalidCounter: parsed.invalidCounter || 0, pausedUntil: parsed.pausedUntil || 0 });
      } else {
        this.stateSubject.next({ invalidCounter: 0, pausedUntil: 0 });
      }
    } catch (e) {
      this.stateSubject.next({ invalidCounter: 0, pausedUntil: 0 });
    }
  }

  getState(): OtpState {
    return this.stateSubject.getValue();
  }

  setState(state: OtpState) {
    try {
      Shared.helpers.CookieHelper.setCookie(this.cookieKey, JSON.stringify(state));
    } catch (e) {
      // ignore
    }
    this.stateSubject.next(state);
  }

  /**
   * Helper to set pausedUntil based on a server-provided lastAttempt value.
   * lastAttempt may be in seconds or ms; this method normalizes it and applies pauseMs (defaults to 5 minutes).
   */
  setPauseFromLastAttempt(lastAttempt: number, pauseMs: number = 5 * 60 * 1000) {
    try {
      let last = lastAttempt || Date.now();
      if (last < 1e12) {
        last = last * 1000;
      }
      const pausedUntil = last + pauseMs;
      const newState: OtpState = { ...this.getState(), pausedUntil };
      this.setState(newState);
      return pausedUntil;
    } catch (e) {
      return 0;
    }
  }

  clearState() {
    try {
      Shared.helpers.CookieHelper.deleteCookie(this.cookieKey);
    } catch (e) {
      // ignore
    }
    this.stateSubject.next({ invalidCounter: 0, pausedUntil: 0 });
  }
}
